import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  AttachFile as AttachFileIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import EmailRecipientsField, {
  areOptionalRecipientsValid, areRecipientsValid
} from './EmailRecipientsField';
import EmailDeliveryHistory from './EmailDeliveryHistory';

const formatFileSize = (size = 0) => {
  if (!Number.isFinite(size) || size <= 0) return '0 KB';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export default function EmailComposerDialog({
  open,
  onClose,
  onSend,
  sending = false,
  title = 'Send Email',
  subtitle = '',
  badge = 'EMAIL',
  form,
  onFormChange,
  template = '',
  templateOptions = [],
  onTemplateChange,
  quickInserts = [],
  onQuickInsert,
  onResetDraft,
  senderLabel,
  replyToEmail,
  contextLabel = 'DOCUMENT',
  contextValue = '',
  senderName = 'SMTLINKS Team',
  previewText = '',
  summaryTitle = 'Summary',
  summaryItems = [],
  attachments = [],
  attachmentLoading = false,
  attachmentError = '',
  onFilesSelected,
  onRemoveAttachment,
  history = [],
  historyLoading = false,
  historyError = '',
  onRefreshHistory,
  workflowStatus = null,
  workflowMessage = '',
  workflowError = '',
  workflowLoading = false,
  workflowActions = [],
  sendDisabled = false,
  sendLabel = 'Send Email',
  formReadOnly = false,
  showFollowUpSettings = false,
  followUpDays = 7,
  onFollowUpDaysChange
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const accentColor = theme.palette.primary.main;

  return (
    <Dialog
      open={open}
      onClose={() => !sending && onClose()}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: alpha(accentColor, 0.22),
          backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.99)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
          boxShadow: `0 24px 60px ${alpha('#000', 0.42)}`
        }
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: alpha(accentColor, 0.18),
          backgroundImage: `linear-gradient(120deg, ${alpha(accentColor, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.08)} 100%)`
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(accentColor, 0.18), color: accentColor, boxShadow: `0 10px 24px ${alpha(accentColor, 0.22)}` }}>
              <EmailIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>{title}</Typography>
              <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={badge}
            sx={{ border: '1px solid', borderColor: alpha(accentColor, 0.22), bgcolor: alpha(accentColor, 0.08), fontWeight: 700 }}
          />
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3, bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.44 : 0.58) }}>
        {workflowStatus && (
          <Alert
            severity={workflowStatus === 'approved' ? 'success' : workflowStatus === 'rejected' ? 'error' : workflowStatus === 'pending_review' ? 'warning' : 'info'}
            sx={{ mb: 2.2, borderRadius: 2 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {workflowStatus === 'approved' ? 'Approved and ready to send' : workflowStatus === 'sending' ? 'Sending in progress' : workflowStatus === 'pending_review' ? 'Pending human approval' : workflowStatus === 'rejected' ? 'Changes requested' : 'AI draft — review required'}
            </Typography>
            {workflowMessage && <Typography variant="body2">{workflowMessage}</Typography>}
          </Alert>
        )}
        {showFollowUpSettings && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              mb: 2.2,
              p: 1.75,
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha(accentColor, 0.22),
              bgcolor: alpha(accentColor, 0.06),
              display: 'flex',
              gap: 2,
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' }
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Automatic follow-up</Typography>
              <Typography variant="body2" color="text.secondary">
                If no client reply is detected, the system will prepare another draft for human review. It will never send automatically.
              </Typography>
            </Box>
            <TextField
              type="number"
              size="small"
              label="Wait days"
              value={followUpDays}
              disabled={formReadOnly || workflowLoading}
              onChange={(event) => onFollowUpDaysChange?.(Math.max(1, Math.min(90, Number(event.target.value) || 1)))}
              inputProps={{ min: 1, max: 90, inputMode: 'numeric' }}
              sx={{ width: { xs: '100%', sm: 130 }, flexShrink: 0 }}
            />
          </Box>
        )}
        {workflowError && <Alert severity="error" sx={{ mb: 2.2 }}>{workflowError}</Alert>}
        <Grid container spacing={2.2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: alpha(accentColor, 0.18), bgcolor: alpha(accentColor, 0.07) }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>FROM</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{senderLabel}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>REPLY-TO</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{replyToEmail}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{contextLabel}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{contextValue || '-'}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <EmailRecipientsField
              label="To"
              value={form.to}
              onChange={(value) => onFormChange({ ...form, to: value })}
              required
              placeholder="client@example.com"
              disabled={formReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <EmailRecipientsField
              label="CC (Optional)"
              value={form.cc}
              onChange={(value) => onFormChange({ ...form, cc: value })}
              placeholder="team@example.com"
              helperText="Everyone added here receives a visible copy."
              disabled={formReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Template"
              value={template}
              onChange={(event) => onTemplateChange(event.target.value)}
              helperText="Apply a starting structure and edit it freely."
              disabled={formReadOnly}
            >
              {templateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Subject"
              value={form.subject}
              onChange={(event) => onFormChange({ ...form, subject: event.target.value })}
              disabled={formReadOnly}
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: alpha(accentColor, 0.18), bgcolor: alpha(accentColor, 0.04) }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 1 }}>QUICK INSERTS</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {quickInserts.map((item) => (
                  <Button key={item.label} size="small" variant="outlined" disabled={formReadOnly} onClick={() => onQuickInsert(item.snippet)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    {item.label}
                  </Button>
                ))}
                <Button size="small" variant="text" disabled={formReadOnly} onClick={onResetDraft} sx={{ textTransform: 'none', fontWeight: 700 }}>Reset draft</Button>
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              multiline
              minRows={10}
              label="Message"
              value={form.message}
              onChange={(event) => onFormChange({ ...form, message: event.target.value })}
              helperText={`This message will be sent as ${senderName} through the corporate mailbox.`}
              disabled={formReadOnly}
            />
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack spacing={2}>
              <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: alpha(theme.palette.divider, 0.85), bgcolor: alpha(theme.palette.background.paper, 0.48) }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>{summaryTitle}</Typography>
                <Stack spacing={1}>
                  {summaryItems.map((item) => (
                    <Box key={item.label}>
                      <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{item.value || 'Not defined'}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <EmailDeliveryHistory
                items={history}
                loading={historyLoading}
                error={historyError}
                onRefresh={onRefreshHistory}
              />

              <Box sx={{ p: 2, borderRadius: 2.5, border: '1px dashed', borderColor: alpha(accentColor, 0.4), bgcolor: alpha(accentColor, 0.05) }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Attachments</Typography>
                    <Typography variant="caption" color="text.secondary">The system document is loaded automatically.</Typography>
                  </Box>
                  <Button component="label" variant="outlined" disabled={formReadOnly} startIcon={<AttachFileIcon />} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Add files
                    <input
                      hidden
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.xml,.txt,.csv"
                      onChange={onFilesSelected}
                    />
                  </Button>
                </Stack>
                {attachmentLoading && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Preparing document attachment...</Typography>
                  </Stack>
                )}
                {attachmentError && <Alert severity="warning" sx={{ mb: 1 }}>{attachmentError}</Alert>}
                {!attachmentLoading && attachments.length > 0 && (
                  <Stack spacing={1}>
                    {attachments.map((attachment, index) => (
                      <Chip
                        key={`${attachment.file.name}-${attachment.file.size}-${index}`}
                        icon={<AttachFileIcon />}
                        label={`${attachment.source === 'system' ? 'System: ' : ''}${attachment.file.name} - ${formatFileSize(attachment.file.size)}`}
                        onDelete={() => onRemoveAttachment(index)}
                        color={attachment.source === 'system' ? 'primary' : 'default'}
                        variant={attachment.source === 'system' ? 'outlined' : 'filled'}
                        sx={{ justifyContent: 'space-between', height: 'auto', '& .MuiChip-label': { display: 'block', whiteSpace: 'normal', py: 0.75 } }}
                      />
                    ))}
                  </Stack>
                )}
                {!attachmentLoading && !attachments.length && (
                  <Typography variant="body2" color="text.secondary">No attachments selected.</Typography>
                )}
              </Box>

              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Replies will be routed directly to <strong>{replyToEmail}</strong>.
              </Alert>

              <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: alpha(theme.palette.divider, 0.85), bgcolor: alpha(theme.palette.background.paper, 0.48) }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Message Preview</Typography>
                <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: alpha(accentColor, 0.16), bgcolor: alpha(theme.palette.background.default, 0.58) }}>
                  <Box sx={{ px: 1.5, py: 1.15, color: '#fff', backgroundImage: 'linear-gradient(135deg, #123b69 0%, #1f7aa8 100%)' }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>SMTLINKS Document Email</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.92 }}>From {senderLabel}</Typography>
                  </Box>
                  <Box sx={{ p: 1.75 }}>
                    {previewText ? previewText.split(/\n{2,}/).map((block, index) => (
                      <Typography key={`preview-${index}`} variant="body2" sx={{ mb: 1.4, whiteSpace: 'pre-line', lineHeight: 1.75 }}>{block}</Typography>
                    )) : <Typography variant="body2" color="text.secondary">No message yet.</Typography>}
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: alpha(accentColor, 0.18), bgcolor: alpha(theme.palette.background.paper, 0.9) }}>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        {workflowActions.map((action) => (
          <Button
            key={action.label}
            variant={action.variant || 'outlined'}
            color={action.color || 'primary'}
            onClick={action.onClick}
            disabled={workflowLoading || sending || action.disabled}
          >
            {workflowLoading && action.loadingAction ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
            {action.label}
          </Button>
        ))}
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
          onClick={onSend}
          disabled={sendDisabled || sending || workflowLoading || attachmentLoading || !areRecipientsValid(form.to) || !areOptionalRecipientsValid(form.cc)}
        >
          {sending ? 'Sending...' : sendLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
